import { TWidgetCommonOption, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, CSSProperties, ChangeEvent, ClipboardEvent, FocusEvent, KeyboardEvent } from 'react';
import { useThemeHook } from '../hooks/themeHook';
import { TCoreInputData } from '../types/data';
import { ALIGNMENT_ENUM, TCoreInputOptions } from '../types/options';
import { mergeWithTheme } from '../utils/styles';
import { buildInputCssVars } from './buildInputCssVars';
import styles from './input.module.css';

export type TCoreInputStyleProps = Pick<
    TCoreInputOptions,
    | 'fillContainer'
    | 'alignment'
    | 'inputBorderRadius'
    | 'textAlign'
    | 'padding'
    | 'margin'
    | 'main'
    | 'onSurface'
    | 'outline'
    | 'error'
    | 'onSurfaceVariant'
    | 'surfaceContainerHighest'
    | 'font'
    | 'fontStyle'
    | 'height'
    | 'hovered'
>;

const ALIGNMENT_CLASS: Record<string, string> = {
    [ALIGNMENT_ENUM.TOP_LEFT]: styles.alignTopLeft,
    [ALIGNMENT_ENUM.TOP_CENTER]: styles.alignTopCenter,
    [ALIGNMENT_ENUM.TOP_RIGHT]: styles.alignTopRight,
    [ALIGNMENT_ENUM.CENTER_LEFT]: styles.alignCenterLeft,
    [ALIGNMENT_ENUM.CENTER]: styles.alignCenter,
    [ALIGNMENT_ENUM.CENTER_RIGHT]: styles.alignCenterRight,
    [ALIGNMENT_ENUM.BOTTOM_LEFT]: styles.alignBottomLeft,
    [ALIGNMENT_ENUM.BOTTOM_CENTER]: styles.alignBottomCenter,
    [ALIGNMENT_ENUM.BOTTOM_RIGHT]: styles.alignBottomRight,
};

type TNativeInputProps = {
    id?: string;
    dataTest?: string;
    className?: string;
    inputRef?: React.RefObject<HTMLInputElement | null>;
    type?: string;
    inputMode?: 'text' | 'numeric' | 'decimal' | 'email';
    autoComplete?: string;
    name?: string;
    maxLength?: number;
    required?: boolean;
    tabIndex?: number;
    autoFocus?: boolean;
    readOnly?: boolean;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
    onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
};

export type TCoreInputViewProps = {
    widget?: TWidget<TCoreInputData, TCoreInputOptions>;
    uid?: string;
    label?: string;
    value?: string;
    helperText?: string;
    isError?: boolean;
    isFocused?: boolean;
    isFilled?: boolean;
    styleProps?: TCoreInputStyleProps;
    renderWrapper?: (input: React.ReactNode) => React.ReactNode;
} & Pick<TWidgetCommonOption, 'isDisabled' | 'isHide' | 'style' | 'parentStyleNames'> &
    TNativeInputProps;

const buildPalette = (themePalette: TCoreInputOptions, styleProps?: TCoreInputStyleProps): TCoreInputOptions => {
    const filteredTheme = Object.fromEntries(Object.entries(themePalette).filter(([, v]) => v !== undefined && v !== null)) as TCoreInputOptions;
    return {
        ...mergeWithTheme(themePalette, styleProps),
        ...(styleProps?.fontStyle || filteredTheme.fontStyle ? { fontStyle: { ...(filteredTheme.fontStyle ?? {}), ...(styleProps?.fontStyle ?? {}) } } : {}),
        ...(styleProps?.padding || filteredTheme.padding ? { padding: { ...(filteredTheme.padding ?? {}), ...(styleProps?.padding ?? {}) } } : {}),
        ...(styleProps?.margin || filteredTheme.margin ? { margin: { ...(filteredTheme.margin ?? {}), ...(styleProps?.margin ?? {}) } } : {}),
    };
};

const CoreInputView = ({
    widget,
    uid,
    id,
    dataTest,
    className,
    label,
    value,
    helperText,
    isError,
    isDisabled,
    isHide,
    isFocused,
    isFilled,
    styleProps,
    style,
    parentStyleNames,
    inputRef,
    type,
    inputMode,
    autoComplete,
    name,
    maxLength,
    required,
    tabIndex,
    autoFocus,
    readOnly,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onPaste,
    onClick,
    renderWrapper,
}: TCoreInputViewProps) => {
    const baseWidget: TWidget<TCoreInputData, TCoreInputOptions> = widget ?? {
        uid: uid ?? '',
        internalId: null,
        id: '',
        alternativeId: '',
        widgets: [],
        data: {},
        options: {},
        systemMetadata: {},
    };
    const enrichedWidget = {
        ...baseWidget,
        options: { ...baseWidget.options, style, parentStyleNames },
    };
    const { palette: themePalette } = useThemeHook(enrichedWidget);
    const palette: TCoreInputOptions = buildPalette(themePalette, styleProps);
    const resolvedIsFilled = isFilled ?? Boolean(value);

    const nativeInput = (
        <input
            ref={inputRef}
            id={id}
            data-test={dataTest}
            className={clsx(!renderWrapper && styles.nativeInput, className)}
            value={value || ''}
            aria-invalid={isError || undefined}
            disabled={isDisabled}
            type={type}
            inputMode={inputMode}
            autoComplete={autoComplete}
            name={name}
            maxLength={maxLength}
            required={required}
            tabIndex={tabIndex}
            autoFocus={autoFocus}
            readOnly={readOnly}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onClick={onClick}
        />
    );

    if (renderWrapper) {
        return <Activity mode={isHide ? 'hidden' : 'visible'}>{renderWrapper(nativeInput)}</Activity>;
    }

    const isFloated = isFocused || resolvedIsFilled;
    const cls = clsx(
        styles.wrapper,
        isFloated && styles.floated,
        isError && styles.error,
        (isDisabled || readOnly) && styles.disabled,
        palette.fillContainer ? styles.fullWidth : styles.fitContent,
        palette.alignment && ALIGNMENT_CLASS[palette.alignment],
    );

    return (
        <Activity mode={isHide ? 'hidden' : 'visible'}>
            <div className={cls} data-test={uid ?? widget?.uid} style={buildInputCssVars(palette) as CSSProperties}>
                <fieldset className={styles.fieldset} aria-hidden="true">
                    <legend className={styles.legend}>
                        <span>{label}</span>
                    </legend>
                </fieldset>
                {label && <label className={styles.label}>{label}</label>}
                {nativeInput}
                {helperText && <span className={styles.helperText}>{helperText}</span>}
            </div>
        </Activity>
    );
};

export { CoreInputView };
